import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import NewFiles from './NewFiles'
import ExistingFiles from './ExistingFiles'
import Form from '../../../../../components/Form'

import { usePurifiedFormik } from '../../../../../hooks/formHooks/usePurifiedFormik'
import usePermissions from '../../../../../hooks/usePermissions'

import { createJsonFromQueryString } from '../../../../../helpers/url'
import { insertIf } from '../../../../../helpers/common'

import { clearUpdateRight, updateRight } from '../../../../../modules/actions/rights'
import {
  updateRightWasUpdatedSelector,
  updateRightIsLoadingSelector,
  updateRightErrorSelector
} from '../../../../../modules/selectors/rights'
import { formItemIdSelector } from '../../../../../modules/selectors/forms'
import { selectedPartnershipSelector } from '../../../../../modules/selectors/partnerships'
import { choicesAllocationUnitsSelector } from '../../../../../modules/selectors/choices'

import { transformRightAllocationUnit } from '../../RightForms/formatters'
import { ALLOCATION_UNIT, REQUEST_CREATIVE_ASSETS, REQUEST_LOGO } from '../../RightForms/fields'
import { EXISTED_FILES, getInitialValues, NEW_FILES, validationSchema } from './fields'
import {
  CREATIVE_ASSETS_FILE_TYPE,
  LOGO_FILE_TYPE,
  partnerFileTypesList,
  rightsHolderFileTypesList,
  sharedFileTypesList
} from '../../../../../pages/Partnerships/PartnershipRightsList/helpers'
import { PARTNER_FILE_TYPES_LIST, RIGHTS_HOLDER_FILE_TYPES_LIST } from '../../../../../constants/permissions'

function ManageRightFilesForm({ selectedRight, formName }) {
  const dispatch = useDispatch()
  const permissions = usePermissions()

  const { [formName]: selectedRightId } = useSelector(formItemIdSelector)
  const rightWasUpdated = useSelector(updateRightWasUpdatedSelector)
  const allocationUnits = useSelector(choicesAllocationUnitsSelector)
  const { id: partnershipId } = useSelector(selectedPartnershipSelector)

  const { preselectedFileType } = createJsonFromQueryString(window.location.search)

  const availableFileTypesList = useMemo(
    () =>
      // insert file types based on user permissions, sort alphabetically
      [
        ...insertIf(permissions.can('manage', RIGHTS_HOLDER_FILE_TYPES_LIST), ...rightsHolderFileTypesList),
        ...insertIf(permissions.can('manage', PARTNER_FILE_TYPES_LIST), ...partnerFileTypesList),
        ...sharedFileTypesList
      ].sort((a, b) => a.label.localeCompare(b.label, { sensitivity: 'case' })),
    [permissions]
  )

  // PUT - will update all files in next way.
  // Files which ids isn't in passed field will be deleted
  // For each record which has id all main fields will be updated.
  // For each record which doesn't have id new file object will be created

  const onSubmit = useCallback(
    values => {
      const rightAllocation = transformRightAllocationUnit(selectedRight, allocationUnits)

      const newFiles = values[NEW_FILES].map(({ name, type, url }) => ({
        document: url,
        right_file_type: type,
        document_name: name
      }))

      const wasLogoSaved = !!newFiles.find(item => item.right_file_type === LOGO_FILE_TYPE)
      const wasCreativeAssetSaved = !!newFiles.find(item => item.right_file_type === CREATIVE_ASSETS_FILE_TYPE)

      const rightData = {
        ...selectedRight,
        files: [...values[EXISTED_FILES], ...newFiles],
        partnership: partnershipId,
        [ALLOCATION_UNIT]: rightAllocation,
        ...(wasLogoSaved && { [REQUEST_LOGO]: false }),
        ...(wasCreativeAssetSaved && { [REQUEST_CREATIVE_ASSETS]: false })
      }

      if (!rightData?.category?.id) {
        // delete category property from rightData
        delete rightData.category
      } else {
        // in response we have category object, but in request we need only id
        rightData.category = rightData.category.id
      }

      dispatch(updateRight(selectedRightId, rightData, 'PUT'))
    },
    [dispatch, selectedRight, selectedRightId, allocationUnits, partnershipId]
  )

  const clearUpdateRightHandler = useCallback(() => dispatch(clearUpdateRight()), [dispatch])

  const formik = useFormik({
    initialValues: getInitialValues({
      existedFiles: selectedRight?.files,
      preselectedFileType,
      availableFileTypesList
    }),
    enableReinitialize: true,
    validationSchema,
    onSubmit
  })
  const purifiedFormik = usePurifiedFormik(formik)
  const existedFiles = purifiedFormik.values[EXISTED_FILES]

  return (
    <Form
      formName={formName}
      formik={purifiedFormik}
      clearHandler={clearUpdateRightHandler}
      isLoadingSelector={updateRightIsLoadingSelector}
      errorSelector={updateRightErrorSelector}
      successSubmit={rightWasUpdated}
    >
      {existedFiles && !!existedFiles.length && (
        <ExistingFiles files={existedFiles} setFieldValue={purifiedFormik.setFieldValue} />
      )}
      <NewFiles formik={purifiedFormik} availableFileTypesList={availableFileTypesList} />
    </Form>
  )
}

export default ManageRightFilesForm
